import { knowledgeBaseService, type KnowledgeBase } from '../services/knowledgeBaseService';
import { projectService } from '../services/projectService';
import { subProjectService } from '../services/subProjectService';
import type { EntitySCV2Config, KBActionState } from './entitySCV2Config';
import RAGStatusBadge from '../components/RAGStatusBadge';
import RAGProgressBar from '../components/RAGProgressBar';

// Field permissions for KB entity
const KB_FIELDS_PERMISSIONS = {
  name: 'none' as const,
  description: 'editable' as const,
  owner_id: 'none' as const,
  project_id: 'none' as const,
  sub_project_id: 'none' as const,
  document_count: 'display' as const,
  created_date: 'none' as const,
  last_updated: 'none' as const,
  id: 'none' as const,
};

// Field labels and descriptions
const KB_FIELD_LABELS = {
  name: {
    label: 'Knowledge Base Name',
    description: 'The official name of the knowledge base',
  },
  description: {
    label: 'Description',
    labelKey: 'scv2.fields.description',
    description: 'Description of the knowledge base content and purpose',
  },
  document_count: {
    label: 'Document Count',
    labelKey: 'scv2.fields.document_count',
    description: 'Number of documents in this knowledge base',
  },
  created_date: {
    label: 'Created Date',
    description: 'When the knowledge base was created',
  },
  last_updated: {
    label: 'Last Updated',
    description: 'When the knowledge base was last modified',
  },
};

// Format date helper
const formatDate = (dateString: string, language: string): string => {
  return new Date(dateString).toLocaleDateString(
    language === 'fr' ? 'fr-FR' : 'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );
};

// KB SCV2 Configuration
export const kbSCV2Config: EntitySCV2Config<KnowledgeBase> = {
  type: 'kb',
  displayName: 'Knowledge Bases',

  fieldsPermissions: KB_FIELDS_PERMISSIONS,
  fieldLabels: KB_FIELD_LABELS,

  service: {
    get: (id: string) => knowledgeBaseService.getById(id),
    update: (id: string, data: Partial<KnowledgeBase>) => knowledgeBaseService.update(id, data),
    delete: (id: string) => knowledgeBaseService.delete(id),
  },

  buildBreadcrumbs: async (kb: KnowledgeBase) => {
    const breadcrumbs = [
      { path: '/knowledge-bases', label: 'Knowledge Bases' },
    ];

    // Try to load parent project/sub-project for context
    try {
      if (kb.sub_project_id) {
        const subProject = await subProjectService.getById(kb.sub_project_id);
        const project = await projectService.getProject(subProject.project_id);
        breadcrumbs.push(
          { path: '/projects', label: 'Projects' },
          { path: `/project/${project.id}`, label: project.name },
          { path: `/scv2/sub-project/${subProject.id}`, label: subProject.name }
        );
      } else if (kb.project_id) {
        const project = await projectService.getProject(kb.project_id);
        breadcrumbs.push(
          { path: '/projects', label: 'Projects' },
          { path: `/project/${project.id}`, label: project.name }
        );
      }
    } catch (err) {
      console.warn('Failed to load parent project/sub-project for breadcrumb:', err);
    }

    breadcrumbs.push({ path: `/knowledge-base/${kb.id}`, label: kb.name });

    return breadcrumbs;
  },

  buildActionButtons: (_kb, handlers, permissions, t, kbState?: KBActionState) => {
    const ragStatus = kbState?.ragStatus;
    const ragPrepareLoading = kbState?.ragPrepareLoading;
    const canPrepareRag = kbState?.canPrepareRag ?? false;
    const isPreparing = ragStatus === 'preparing';
    const isLkb = (_kb as { kb_class?: string } | undefined)?.kb_class === 'reference_large';

    return (
      <>
        {/* RAG Status Box */}
        <div className="rag-status-box" title="View RAG status">
          <RAGStatusBadge status={ragStatus} />
        </div>

        {/* LKB: a single button routing to the RAG status page, which hosts the SysAdmin (re)build
            + live status. LKBs are NOT prepared via the SKB chunk pipeline. */}
        {isLkb ? (
          <button
            className="btn btn-compact btn-primary"
            onClick={handlers.onPrepareRag}
            title={t('kbDetail.lkbStatusHint', 'View / build the LKB index')}
          >
            <span className="btn-icon">&#9881;</span>
            {t('kbDetail.lkbStatus', 'LKB index')}
          </button>
        ) : (
          /* Prepare RAG / RAG Status Button (SKB) */
          <button
            className={`btn btn-compact ${ragStatus === 'failed' ? 'btn-danger' : 'btn-primary'}`}
            onClick={handlers.onPrepareRag}
            disabled={!canPrepareRag || ragPrepareLoading || isPreparing}
            title={!canPrepareRag ? 'Insufficient permissions' : ragStatus === 'ready' ? t('kbDetail.ragStatusHint', 'View RAG status') : ragStatus === 'failed' ? t('kbDetail.ragStatusFailedHint', 'View RAG status (failed)') : t('kbDetail.prepareRagHint', 'Prepare RAG (incremental)')}
          >
            <span className="btn-icon">&#9881;</span>
            {(ragPrepareLoading || isPreparing)
              ? t('kbDetail.prepareRagLoading', 'Preparing...')
              : ragStatus === 'ready'
                ? t('kbDetail.ragStatus', 'RAG Status')
                : ragStatus === 'failed'
                  ? t('kbDetail.ragStatusFailed', 'RAG Status (failed)')
                  : t('kbDetail.prepareRag', 'Prepare RAG')
            }
          </button>
        )}

        {/* RAG Progress Bar (shown while preparing) */}
        {!isLkb && isPreparing && kbState?.ragProgress && (
          <div style={{ width: '100%' }}>
            <RAGProgressBar
              progress={kbState.ragProgress}
              message={kbState.ragMessage ?? null}
              vectorStatus={kbState.ragVectorStatus ?? null}
              bm25Status={kbState.ragBm25Status ?? null}
            />
          </div>
        )}

        {/* Add Documents Button */}
        <button
          className="btn btn-primary btn-compact"
          onClick={handlers.onAddDocuments}
          title={t('kbDetail.addDocumentsHint', 'Add docs to this knowledge base')}
        >
          <span className="btn-icon">&#10133;</span>
          {t('kbDetail.addDocuments', 'Add Docs')}
        </button>

        {/* Delete Selected Documents */}
        <button
          className="btn btn-danger btn-compact"
          onClick={handlers.onDeleteSelectedDocuments}
          disabled={(kbState?.selectedDocumentCount || 0) === 0}
          title={t('kbDetail.deleteSelectedHint', 'Delete selected docs from this knowledge base')}
        >
          <span className="btn-icon">&#128465;</span>
          {t('kbDetail.deleteSelectedDocuments', 'Delete selected docs')}
        </button>

        {/* Delete KB Button */}
        {permissions.canDelete && (
          <button
            className="btn btn-danger btn-compact"
            onClick={handlers.onDelete}
            title={t('kbDetail.deleteKbHint', 'Delete this knowledge base')}
          >
            <span className="btn-icon">&#128465;</span>
            {t('kbDetail.deleteKb', 'Delete KB')}
          </button>
        )}
      </>
    );
  },

  getDetailPath: (kb: KnowledgeBase) => `/knowledge-base/${kb.id}`,
  
  getParentPath: (kb: KnowledgeBase) => {
    if (kb.sub_project_id) return `/scv2/sub-project/${kb.sub_project_id}`;
    if (kb.project_id) return `/project/${kb.project_id}`;
    return '/knowledge-bases';
  },

  formatDate,

  getKbIds: (kb: KnowledgeBase) => [kb.id],

  getEntityName: (kb: KnowledgeBase) => kb.name,
};
